import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { SharedTreatExperimentMutation, SharedTreatExperimentMutationVariables } from './treatExperiment.graphql';
import { SharedTreatExperiment } from './treatExperiment.graphql';

/**
 * Treats an experiment by calling the GraphQL API.
 *
 * This should be awaited so that the treated state is registered on the server before any subsequent tracking event.
 *
 * Returns whether the treatment was actually registered server-side (`treated`), not just whether
 * the mutation round-tripped without throwing — a bot session or a not-yet-treatable assignment can
 * resolve without error while `treated` is still `false`, so callers gating behavior (e.g. firing
 * `$exposure`) on real registration need this distinction.
 */
export async function treatExperiment(utilityContext: UtilityContext, experimentName: string): Promise<boolean> {
	try {
		const { data } = await utilityContext.gql.fetch<
			SharedTreatExperimentMutation,
			SharedTreatExperimentMutationVariables
		>({
			query: SharedTreatExperiment,
			variables: { experimentName },
			rejectOnError: true,
		});
		return data.treatExperiment.treated;
	} catch (e) {
		// we don't want this to break the app, so we make it fail silently
		// error will still be reported by GqlClient
		return false;
	}
}
